import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";
import { PostHogProvider } from "posthog-js/react";
import { PostHogConfig } from "posthog-js";
import { ToastContainer } from "react-toastify";
import { Tooltip } from "react-tooltip";
import styled from "styled-components";
import {
  initIntl,
  IS_DEMO,
  LOGOUT_USER,
  REFRESH_TOKEN,
  DARK_BLUE,
  FROSTED_BLACK,
  getStore,
  applicationActions,
  AppStore,
} from "@shape/reef-shared";

import Navbar from "components/Navbar";
import TopBar from "components/TopBar";
import { RoutesContainer } from "./RoutesContainer";
import { Routes } from "routes";

import "./App.scss";

const SwitchContainer = styled.div<{ isLoginPage: boolean }>`
  margin-left: ${(props) => (props.isLoginPage ? "0" : "72px")};
  height: 100%;
`;
const AppContainer = styled.div`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const MainContent = styled.div<{ isNavBarExpanded: boolean }>`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  filter: ${(props) => (props.isNavBarExpanded ? "opacity(0.5)" : "unset")};
  pointer-events: ${(props) => (props.isNavBarExpanded ? "none" : "unset")};
  transition: 0.2s ease;
  background-color: ${FROSTED_BLACK};
  overflow: auto;
`;

const RELOAD_ATTEMPTS_KEY = "page-reload-attempts";
const MAX_RELOAD_ATTEMPTS = 5;

const store: AppStore = getStore();
Modal.setAppElement("#root");
initIntl();

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === Routes.LoginPage;

  return <SwitchContainer isLoginPage={isLoginPage}>{children}</SwitchContainer>;
};

export const App: React.FC = () => {
  const [isNavBarExpanded, setIsNavBarExpanded] = useState(false);

  const handlePreloadError = useCallback((event: Event) => {
    event.preventDefault();
    const reloadCount = Number(sessionStorage.getItem(RELOAD_ATTEMPTS_KEY) ?? "0");
    if (reloadCount < MAX_RELOAD_ATTEMPTS) {
      sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(reloadCount + 1));
      const url = new URL(globalThis.location.href);
      url.searchParams.set("_t", Date.now().toString());
      globalThis.location.replace(url.toString());
    }
  }, []);

  useEffect(() => {
    globalThis.addEventListener("vite:preloadError", handlePreloadError);
    return () => globalThis.removeEventListener("vite:preloadError", handlePreloadError);
  }, [handlePreloadError]);

  const options: Partial<PostHogConfig> = {
    api_host:
      import.meta.env.VITE_ENVIRONMENT === "development" || IS_DEMO
        ? import.meta.env.VITE_PUBLIC_POSTHOG_HOST
        : `${window.location.origin}/posthog_proxy`,
    persistence: "localStorage" as "localStorage" | "cookie" | "memory",
  };

  useEffect(() => {
    window.addEventListener(REFRESH_TOKEN, ((event: CustomEvent) => {
      store.dispatch(applicationActions.setToken(event.detail));
    }) as EventListener);

    window.addEventListener(LOGOUT_USER, (() => {
      store.dispatch(applicationActions.clearUserData());
    }) as EventListener);
  }, []);

  return (
    <Provider store={store}>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        toastStyle={{ minWidth: "360px" }}
        closeButton
      />
      <BrowserRouter>
        <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY ?? ""} options={options}>
          <Tooltip
            anchorSelect={".grid-tooltip"}
            clickable
            style={{ zIndex: "9", backgroundColor: DARK_BLUE, maxWidth: "30vw" }}
            opacity={1}
          />
          <Navbar onExpand={setIsNavBarExpanded} isExpanded={isNavBarExpanded} />
          <Wrapper>
            <AppContainer>
              <TopBar />
              <MainContent isNavBarExpanded={isNavBarExpanded}>
                <RoutesContainer />
              </MainContent>
            </AppContainer>
          </Wrapper>
        </PostHogProvider>
      </BrowserRouter>
    </Provider>
  );
};
